import type { TimePreset, WeatherPreset, WeatherLightingPreset } from '../../../types';

/** Degrees to radians conversion */
export const DEG_TO_RAD = Math.PI / 180;
/** Radians to degrees conversion */
export const RAD_TO_DEG = 180 / Math.PI;

/** Default distance for sun/moon light from scene center */
export const DEFAULT_LIGHT_DISTANCE = 200;

/** Time presets mapped to approximate sun elevations */
export const TIME_PRESET_ELEVATIONS: Record<TimePreset, { elevation: number; azimuthOffset: number }> = {
	sunrise: { elevation: 5, azimuthOffset: -90 },    // East, just above horizon
	morning: { elevation: 30, azimuthOffset: -45 },   // Southeast
	afternoon: { elevation: 60, azimuthOffset: 0 },   // South (peak)
	evening: { elevation: 30, azimuthOffset: 45 },    // Southwest
	sunset: { elevation: 5, azimuthOffset: 90 },      // West, just above horizon
	night: { elevation: -20, azimuthOffset: 180 }     // Below horizon (moon will be shown)
};

/** Weather presets with lighting configurations */
export const WEATHER_PRESETS: Record<WeatherPreset, WeatherLightingPreset> = {
	'clear-sunny': {
		ambientIntensity: 0.3,
		ambientColor: 0xffffff,
		directionalIntensity: 2.5,
		directionalColor: 0xfffaf0, // Warm white
		hemisphereSkyColor: 0x87ceeb, // Sky blue
		hemisphereGroundColor: 0xffe4b5, // Moccasin warm
		hemisphereIntensity: 0.6,
		shadowSoftness: 0.1,
		shadowsEnabled: true
	},
	'overcast': {
		ambientIntensity: 0.8,
		ambientColor: 0xe8e8e8,
		directionalIntensity: 0.5,
		directionalColor: 0xd0d0d0, // Gray
		hemisphereSkyColor: 0x9ca3af, // Gray
		hemisphereGroundColor: 0x9ca3af, // Gray
		hemisphereIntensity: 0.7,
		shadowSoftness: 0.9,
		shadowsEnabled: true
	},
	'cloudy': {
		ambientIntensity: 0.6,
		ambientColor: 0xf0f0f0,
		directionalIntensity: 1.2,
		directionalColor: 0xe0e0e0,
		hemisphereSkyColor: 0xb0b8c0, // Blue-gray
		hemisphereGroundColor: 0xd4c4a8, // Warm tan
		hemisphereIntensity: 0.5,
		shadowSoftness: 0.5,
		shadowsEnabled: true
	},
	'twilight': {
		ambientIntensity: 0.2,
		ambientColor: 0x4a3c6e, // Purple tint
		directionalIntensity: 0.8,
		directionalColor: 0xff8c42, // Orange
		hemisphereSkyColor: 0x5c4b8a, // Purple
		hemisphereGroundColor: 0xffa54f, // Orange
		hemisphereIntensity: 0.4,
		shadowSoftness: 0.7,
		shadowsEnabled: true
	},
	'rainy': {
		ambientIntensity: 0.5,
		ambientColor: 0xb8c4cc,
		directionalIntensity: 0.3,
		directionalColor: 0xa0a8b0,
		hemisphereSkyColor: 0x6b7280, // Dark gray
		hemisphereGroundColor: 0x4b5563, // Darker gray
		hemisphereIntensity: 0.3,
		shadowSoftness: 1.0,
		shadowsEnabled: false
	},
	'golden-hour': {
		ambientIntensity: 0.4,
		ambientColor: 0xffd89b,
		directionalIntensity: 2.0,
		directionalColor: 0xffa040, // Golden orange
		hemisphereSkyColor: 0xffb347, // Orange
		hemisphereGroundColor: 0xffd700, // Gold
		hemisphereIntensity: 0.5,
		shadowSoftness: 0.3,
		shadowsEnabled: true
	},
	'blue-hour': {
		ambientIntensity: 0.3,
		ambientColor: 0x4a6fa5,
		directionalIntensity: 0.6,
		directionalColor: 0x6b8cae, // Blue
		hemisphereSkyColor: 0x4169e1, // Royal blue
		hemisphereGroundColor: 0x2c3e50, // Dark blue-gray
		hemisphereIntensity: 0.4,
		shadowSoftness: 0.6,
		shadowsEnabled: true
	}
};
